import InfoIcon from "@/assets/colorable/info_filled.svg?react";
import InputField from "@/components/Common/forms/InputField";
import Toggle from "@/components/Common/forms/Toggle";
import DisciplineTemplateForm from "@/components/discipline/DisciplineTemplateForm";
import FlaggedCampaign from "@/components/discipline/FlaggedCampaign";
import InfoboxSection from "@/components/discipline/InfoboxSection";
import RestrictionSection from "@/components/discipline/RestrictionSection";
import VerificationButton from "@/components/discipline/VerificationButton";
import useCountdown from "@/hooks/useCountdown";
import {useDiscipline} from "@/hooks/useDiscipline";
import clsxm from "@/lib/clsxm";
import {disciplinaryModalAtom, disciplineTabAtom} from "@/store/jotaiAtoms";
import {getDisciplineColor, getRemainingTime, isUnverifiedType} from "@/utils/disciplineUtils";
import {useAtom, useAtomValue} from "jotai";
import {memo, useEffect, useMemo, type FC} from "react";
import {useTranslation} from "react-i18next";

const DisciplineContent: FC<{
  durationTitleKey?: string;
  showDays?: boolean;
  showToggle?: boolean;
  showTypeSwitcher?: boolean;
  toggleTitleKey?: string;
}> = ({durationTitleKey, showDays = true, showToggle, showTypeSwitcher, toggleTitleKey}) => {
  const {disciplineTabInPunishmentType, invalidateQueries, isStrongerDisciplineActive} = useDiscipline();
  const {t} = useTranslation("influencer_discipline");
  const [disciplinaryData, setDisciplinaryData] = useAtom(disciplinaryModalAtom);
  const disciplineTab = useAtomValue(disciplineTabAtom);

  const {selectedDiscipline} = disciplinaryData;

  const {remainingDays, remainingHours, remainingMinutes} = useMemo(
    () => getRemainingTime(selectedDiscipline?.expiration_date),
    [selectedDiscipline?.expiration_date],
  );

  useEffect(() => {
    if (selectedDiscipline) {
      setDisciplinaryData(draft => {
        if (disciplineTab === "UNVERIFY") {
          draft.isTimeToggleEnabled = selectedDiscipline?.re_verification_allowed ?? true;
        } else {
          draft.isTimeToggleEnabled = true;
        }

        draft.disciplineDuration = remainingDays;
      });
    } else {
      setDisciplinaryData(draft => {
        draft.isTimeToggleEnabled = true;
        draft.disciplineDuration = 0;
      });
    }
  }, [disciplineTab, setDisciplinaryData, selectedDiscipline, remainingDays]);

  const {countdown: minutesCountdown, isCountdownFinished} = useCountdown({
    // Add 2 extra minutes to the countdown to delay the refetch slightly, since the backend might not update immediately when time runs out.
    // We only apply this delay if less than 15 minutes remain, as it's unnecessary for longer durations.
    from:
      remainingDays === 0 && remainingHours === 0 && remainingMinutes < 15 ? remainingMinutes + 2 : remainingMinutes,
    interval: 60,
    shouldStartCountdown: true,
  });

  useEffect(() => {
    if (isCountdownFinished && remainingDays === 0 && remainingHours === 0 && selectedDiscipline?.id) {
      invalidateQueries();
      setDisciplinaryData(draft => {
        if (selectedDiscipline?.id === disciplinaryData.lastDiscipline?.id) {
          draft.lastDiscipline = null;
        }

        draft.selectedDiscipline = null;
      });
    }
  }, [
    invalidateQueries,
    isCountdownFinished,
    remainingDays,
    remainingHours,
    remainingMinutes,
    selectedDiscipline?.id,
    disciplinaryData.lastDiscipline?.id,
    setDisciplinaryData,
  ]);

  return (
    <div className={clsxm("flex flex-col gap-6 px-5 py-6", isStrongerDisciplineActive && "bg-neutral-grey-light-dark")}>
      {!selectedDiscipline &&
        !disciplinaryData.campaignApplication &&
        !isUnverifiedType(disciplineTabInPunishmentType) && (
          <div className="bg-neutral-white-dark border-neutral-grey-light-dark flex items-center gap-2 rounded-md border p-3">
            <InfoIcon
              className="size-3.5 shrink-0"
              style={{color: `var(--color-${getDisciplineColor(disciplineTabInPunishmentType)})`}}
            />
            <span
              className="text-sm"
              style={{color: `var(--color-${getDisciplineColor(disciplineTabInPunishmentType)})`}}>
              {t("missing_campaign_application_description")}
            </span>
          </div>
        )}
      {disciplineTab !== "UNVERIFY" && <InfoboxSection />}
      <FlaggedCampaign disciplineId={selectedDiscipline?.id} />
      {showTypeSwitcher && (
        <div className="text-neutral-black-dark flex flex-col gap-2">
          <span className="text-xs">{t("verifications")}</span>
          <VerificationButton />
        </div>
      )}
      {disciplinaryData.selectedUnverificationType === "ugc" && (
        <div className="bg-neutral-white-dark border-neutral-grey-light-dark flex items-center gap-2 rounded-md border p-3">
          <InfoIcon
            className="size-3.5 shrink-0"
            style={{color: `var(--color-${getDisciplineColor(disciplineTabInPunishmentType)})`}}
          />
          <span
            className="text-sm"
            style={{color: `var(--color-${getDisciplineColor(disciplineTabInPunishmentType)})`}}>
            {t("ugc_unverification_info")}
          </span>
        </div>
      )}
      {showDays && (
        <div className="text-neutral-black-dark flex flex-col gap-6">
          {showToggle && (
            <div className="flex flex-col gap-2">
              {toggleTitleKey && <span className="text-xs">{t(toggleTitleKey)}</span>}
              <Toggle
                checked={!!disciplinaryData?.isTimeToggleEnabled}
                disabled={isStrongerDisciplineActive}
                onChange={value =>
                  setDisciplinaryData(draft => {
                    draft.isTimeToggleEnabled = value;
                  })
                }
              />
            </div>
          )}
          {disciplinaryData?.isTimeToggleEnabled && showDays && (
            <div className="flex flex-col gap-2">
              {durationTitleKey && <span className="text-sm font-semibold">{t(durationTitleKey)}</span>}
              <div className="flex">
                <div className="flex flex-col gap-1">
                  <span className="px-1 text-sm font-semibold">{t("days")}</span>
                  <InputField
                    className="border-neutral-grey-light-dark h-10 w-12 rounded-lg p-3 text-center"
                    isDisabled={isStrongerDisciplineActive}
                    type="number"
                    value={disciplinaryData?.disciplineDuration?.toString()}
                    onChange={value =>
                      setDisciplinaryData(draft => {
                        draft.disciplineDuration = +value;
                      })
                    }
                  />
                </div>
                {selectedDiscipline && (
                  <>
                    <div className="flex flex-col gap-1">
                      <span className="px-1 text-center text-sm font-semibold">{t("hrs")} </span>
                      <div className="flex items-center">
                        <span className="mx-1">:</span>
                        <InputField
                          isDisabled
                          className="border-neutral-grey-light-dark h-10 w-12 rounded-lg p-3 text-center"
                          type="number"
                          value={remainingHours.toString()}
                          onChange={value =>
                            setDisciplinaryData(draft => {
                              draft.disciplineDuration = +value;
                            })
                          }
                        />
                      </div>
                    </div>
                    <div className="flex flex-col gap-1">
                      <span className="px-1 text-center text-sm font-semibold">{t("min")} </span>
                      <div className="flex items-center">
                        <span className="mx-1">:</span>
                        <InputField
                          isDisabled
                          className="border-neutral-grey-light-dark h-10 w-12 rounded-lg p-3 text-center"
                          type="number"
                          value={minutesCountdown.toString()}
                          onChange={value =>
                            setDisciplinaryData(draft => {
                              draft.disciplineDuration = +value;
                            })
                          }
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      )}
      <RestrictionSection />
      <DisciplineTemplateForm
        hideFormConfig={!!(selectedDiscipline && selectedDiscipline.punishment_type === disciplineTabInPunishmentType)}
        isDisabled={isStrongerDisciplineActive}
        selectedPunishmentType={disciplineTabInPunishmentType}
      />
    </div>
  );
};

export default memo(DisciplineContent);
